import { Component } from '@theme/component';

/**
 * Magento-parity homepage hero: fade carousel with adaptive height (Slick-style).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement[]} [dots]
 *
 * @extends {Component<Refs>}
 */
class MgHeroBanner extends Component {
  #current = 0;

  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {MediaQueryList|undefined} */
  #reducedMotion = undefined;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#onReducedMotionChange);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#onResize);
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    this.#bindImageLoads();
    this.current = this.#initialIndex;
    this.#syncHeight(true);
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.#reducedMotion?.removeEventListener('change', this.#onReducedMotionChange);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#onResize);
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {number|string} index
   */
  select(index) {
    const next = typeof index === 'string' ? parseInt(index, 10) : index;
    if (Number.isNaN(next)) return;
    this.current = next;
    // Manual slide control pauses autoplay (WCAG 2.2.2 mechanism via existing dots).
    this.pause();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#prefersReducedMotion) return;

    this.paused = false;
    this.suspend();

    this.#interval = window.setInterval(() => {
      if (document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    if (this.#interval !== undefined) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  resume() {
    if (!this.autoplay || this.paused || this.#prefersReducedMotion) return;
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get #initialIndex() {
    const value = parseInt(this.getAttribute('initial-slide') || '0', 10);
    return Number.isNaN(value) ? 0 : value;
  }

  get #prefersReducedMotion() {
    return Boolean(this.#reducedMotion?.matches);
  }

  get #slideCount() {
    return (this.refs.slides ?? []).length;
  }

  get #slidesTrack() {
    return this.querySelector('.mg-hero-banner__slides');
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const count = this.#slideCount;
    if (count === 0) return;

    let relativeIndex = current % count;
    if (relativeIndex < 0) relativeIndex += count;

    this.#current = relativeIndex;

    this.refs.slides?.forEach((slide, index) => {
      const isActive = index === relativeIndex;
      slide.setAttribute('aria-hidden', `${!isActive}`);
      slide.toggleAttribute('data-active', isActive);

      slide.querySelectorAll('a.mg-hero-banner__cta').forEach((cta) => {
        if (!(cta instanceof HTMLAnchorElement)) return;
        if (isActive) {
          cta.removeAttribute('tabindex');
        } else {
          cta.setAttribute('tabindex', '-1');
        }
      });
    });

    this.refs.dots?.forEach((dot, index) => {
      const isActive = index === relativeIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.#syncHeight(false);
  }

  /**
   * Match track height to the active slide image (Slick adaptiveHeight).
   * @param {boolean} immediate
   */
  #syncHeight(immediate = false) {
    const track = this.#slidesTrack;
    const active = this.refs.slides?.[this.#current];
    if (!track || !active) return;

    const media = active.querySelector('.mg-hero-banner__media');
    const height = media instanceof HTMLElement ? media.offsetHeight : active.offsetHeight;
    if (!height) return;

    if (immediate || this.#prefersReducedMotion) {
      const previous = track.style.transition;
      track.style.transition = 'none';
      track.style.height = `${height}px`;
      // Force reflow so later transitions still animate
      void track.offsetHeight;
      track.style.transition = previous;
    } else {
      track.style.height = `${height}px`;
    }

    this.toggleAttribute('data-height-ready', true);
  }

  #bindImageLoads() {
    this.querySelectorAll('.mg-hero-banner__image').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      if (img.complete) return;
      img.addEventListener('load', () => this.#syncHeight(true), { once: true });
    });
  }

  #onResize = () => {
    this.#syncHeight(true);
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else {
      this.resume();
    }
  };

  #onReducedMotionChange = () => {
    if (this.#prefersReducedMotion) {
      this.suspend();
    } else {
      this.resume();
    }
    this.#syncHeight(true);
  };

  #onFocusIn = () => {
    this.suspend();
  };

  /**
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    // Do not restart after a user pause (dot selection).
    this.resume();
  };
}

if (!customElements.get('mg-hero-banner-component')) {
  customElements.define('mg-hero-banner-component', MgHeroBanner);
}
